import React, { ReactElement, useState, useEffect, useRef } from 'react';
import { Flex, Spin, Typography } from 'antd';

interface ChatMessageProps {
    message: string,
    expertname?: string,
    isexpert: boolean,
    avatar?: ReactElement,
    isPlaceholder?: boolean,
    containerRef: React.RefObject<HTMLDivElement>,
    useWindowScroll?: boolean
}

function scrollToBottom(ref: React.RefObject<HTMLDivElement>, useWindowScroll?: boolean) {
    if (useWindowScroll) {
        const limit = Math.max(
            document.body.scrollHeight,
            document.body.offsetHeight,
            document.documentElement.clientHeight,
            document.documentElement.scrollHeight,
            document.documentElement.offsetHeight
        );
        window.scrollTo({
            top: limit,
            left: 0,
            behavior: "smooth",
        });
    } else if (ref.current) {
        ref.current.scrollTo({
            top: ref.current.scrollHeight,
            left: 0,
            behavior: "smooth",
        });
    }
}

// Split message into sentences, handling edge cases like:
// - Initials (J.K. Rowling, U.S.A.)
// - Common abbreviations (Mr., Mrs., Dr., Prof., etc., e.g., i.e.)
// - Periods inside quotes ("Hello." he said)
// - Decimal numbers (3.14, $9.99)
function splitIntoSentences(text: string): string[] {
    if (!text) return [];
    
    // Common abbreviations that shouldn't end sentences
    const abbreviations = [
        'Mr', 'Mrs', 'Ms', 'Dr', 'Prof', 'Sr', 'Jr', 'vs', 'etc', 'al',
        'e\\.g', 'i\\.e', 'viz', 'cf', 'approx', 'apt', 'dept', 'est',
        'min', 'max', 'misc', 'no', 'esp', 'fig', 'inc', 'ltd', 'co'
    ];
    
    // Placeholder system to protect patterns that shouldn't be split
    const placeholders: string[] = [];
    let protectedText = text;
    
    const protect = (pattern: RegExp) => {
        protectedText = protectedText.replace(pattern, (match) => {
            placeholders.push(match);
            return `\x00${placeholders.length - 1}\x00`;
        });
    };
    
    // Protect abbreviations followed by a period (case-insensitive)
    const abbrPattern = new RegExp(`\\b(${abbreviations.join('|')})\\.`, 'gi');
    protect(abbrPattern);
    
    // Protect single letter initials (e.g., J.K., U.S.A., A.I.)
    // Matches patterns like "J." or "J.K." or "U.S.A."
    protect(/\b([A-Z]\.)+(?=[A-Z]\.|\s|$)/g);
    
    // Protect periods inside quotes (single and double)
    // e.g., "Hello." or 'Hi.'
    protect(/["'][^"']*[.!?]+["']/g);
    
    // Protect decimal numbers (e.g., 3.14, $9.99, 0.5)
    protect(/\d+\.\d+/g);
    
    // Protect ellipsis
    protect(/\.{3,}/g);
    
    // Protect URLs and emails (basic pattern)
    protect(/\S+@\S+\.\S+/g);
    protect(/https?:\/\/\S+/g);
    
    // Now split on actual sentence boundaries
    // A sentence ends with .!? followed by a space and a capital letter, or end of string
    const sentences = protectedText.split(/(?<=[.!?])\s+(?=[A-Z])|(?<=[.!?])$/g);
    
    // Restore placeholders and clean up
    return sentences
        .map(s => {
            // Restore all placeholders
            let restored = s;
            for (let i = 0; i < placeholders.length; i++) {
                restored = restored.replace(`\x00${i}\x00`, placeholders[i]);
            }
            return restored.trim();
        })
        .filter(s => s.length > 0);
}

const ChatMessage: React.FC<ChatMessageProps> = (props) => {
    const [visibleSentences, setVisibleSentences] = useState<string[]>([]);
    const [isComplete, setIsComplete] = useState(false);
    const sentences = useRef<string[]>([]);
    const messageRef = useRef(props.message);

    useEffect(() => {
        scrollToBottom(props.containerRef, props.useWindowScroll);
    }, []);

    useEffect(() => {
        // Reset when message changes
        if (props.message !== messageRef.current) {
            messageRef.current = props.message;
            setVisibleSentences([]);
            setIsComplete(false);
        }

        // Only animate for expert messages that aren't placeholders
        if (!props.isexpert || props.isPlaceholder || !props.message) {
            return;
        }

        sentences.current = splitIntoSentences(props.message);
        
        if (sentences.current.length === 0) return;

        let currentIndex = 0;
        setVisibleSentences([sentences.current[0]]);
        scrollToBottom(props.containerRef, props.useWindowScroll);

        if (sentences.current.length === 1) {
            setIsComplete(true);
            return;
        }

        const interval = setInterval(() => {
            currentIndex++;
            if (currentIndex < sentences.current.length) {
                setVisibleSentences(prev => [...prev, sentences.current[currentIndex]]);
                scrollToBottom(props.containerRef, props.useWindowScroll);
            } else {
                setIsComplete(true);
                clearInterval(interval);
            }
        }, 800); // 800ms delay between sentences

        return () => clearInterval(interval);
    }, [props.message, props.isexpert, props.isPlaceholder, props.containerRef, props.useWindowScroll]);

    // For user messages, show immediately
    if (!props.isexpert) {
        return (
            <Flex justify="flex-end" style={{ width: '100%', margin: "auto", marginBottom: 5 }}>
                <Flex style={{ maxWidth: '90%' }} vertical>
                    <Typography style={{
                        color: '#FbF3F2',
                        backgroundColor: '#032539',
                        padding: '12px 16px',
                        borderRadius: '20px'
                    }}>
                        {props.message}
                    </Typography>
                </Flex>
            </Flex>
        );
    }

    // For placeholder, show spinner
    if (props.isPlaceholder) {
        return (
            <Flex justify="flex-start" style={{ width: '100%', margin: "auto", marginBottom: 15 }}>
                <Flex style={{ maxWidth: '100%' }} vertical>
                    <Typography style={{
                        color: '#032539',
                        backgroundColor: 'rgba(3, 37, 57, 0.1)',
                        padding: '12px 16px',
                        borderRadius: '20px'
                    }}>
                        <Spin />
                    </Typography>
                </Flex>
            </Flex>
        );
    }

    // For expert messages, show sentences one at a time
    return (
        <>
            {visibleSentences.map((sentence, index) => (
                <Flex 
                    key={index} 
                    justify="flex-start" 
                    style={{ width: '100%', margin: "auto", marginBottom: 5 }}
                >
                    <Flex style={{ maxWidth: '100%' }} vertical>
                        <Typography style={{
                            color: '#032539',
                            backgroundColor: 'rgba(3, 37, 57, 0.1)',
                            padding: '12px 16px',
                            borderRadius: '20px',
                            opacity: 1,
                            animation: 'fadeIn 0.3s ease-in',
                        }}>
                            {sentence}
                        </Typography>
                    </Flex>
                </Flex>
            ))}
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </>
    );
};

export { type ChatMessageProps, ChatMessage };